import { useState } from "react";
import { FACTION_META } from "../engine/pieces";
import type { Faction, GameState } from "../engine/types";
import type { Language, MessageKey } from "../i18n/catalog";
import { loadScenario, playableSkirmishes, skirmishPlayerRange, type PlayableSkirmish } from "../scenarios/loader";
import { DiceRollIcon, GameDie } from "../components/GameIcons";

export interface SkirmishSlot {
  control: "HUMAN" | "AI" | "SPECTATOR";
  name: string;
  faction: Faction;
  position: number;
}

const FACTIONS: Faction[] = ["EFA", "REDCAT", "GREEN", "BLUE"];
const defaults = (language: Language, count=2): SkirmishSlot[] => FACTIONS.slice(0,count).map((faction,index) => ({
  control: index === 0 ? "HUMAN" : "AI",
  name: index === 0 ? text(language, "Hráč 1", "Player 1", "Spieler 1") : `${text(language, "Počítač", "Computer", "Computer")} ${index}`,
  faction, position: index,
}));


const text = (language: Language, cs: string, en: string, de: string) =>
  language === "cs" ? cs : language === "de" ? de : en;

export function configureSkirmishState(state: GameState, slots: SkirmishSlot[], rolls: number[], language: Language, spectator=false): GameState {
  const next = structuredClone(state);
  const neutralPlantIds = next.scenarioId === "skirmish_01"
    ? new Set(next.pieces.filter(piece => piece.type === "POWER_PLANT" && [[7, 7], [13, 7]].some(([col, row]) => piece.col === col && piece.row === row)).map(piece => piece.id))
    : new Set<number>();
  const sourceForPosition: Faction[] = ["EFA", "REDCAT", "GREEN", "BLUE"];
  const replacement = new Map<Faction, Faction>();
  slots.forEach(slot => replacement.set(sourceForPosition[slot.position], slot.faction));
  next.pieces = next.pieces.filter(piece => replacement.has(piece.owner) || neutralPlantIds.has(piece.id));
  next.pieces.forEach(piece => {
    if (neutralPlantIds.has(piece.id)) return;
    const owner = replacement.get(piece.owner);
    if (owner) piece.owner = owner;
  });
  const usedFactions = new Set(slots.map(slot => slot.faction));
  const neutralFaction = FACTIONS.find(faction => !usedFactions.has(faction)) ?? "BLUE";
  next.pieces.forEach(piece => { if (neutralPlantIds.has(piece.id)) piece.owner = neutralFaction; });
  for (const faction of FACTIONS) {
    next.activePlayers[faction] = false;
    next.aiPlayers[faction] = false;
    next.teams[faction] = 9;
    next.startPositions[faction] = 0;
    next.startingDiceRolls[faction] = 0;
  }
  slots.forEach((slot, index) => {
    next.activePlayers[slot.faction] = true;
    next.aiPlayers[slot.faction] = slot.control === "AI";
    next.teams[slot.faction] = index;
    next.startPositions[slot.faction] = slot.position;
    next.startingDiceRolls[slot.faction] = rolls[index];
    next.players[slot.faction].credits = state.players[sourceForPosition[slot.position]].credits;
    next.players[slot.faction].name = slot.control === "AI" ? text(language, "Počítač", "Computer", "Computer") : slot.name.trim() || text(language, `Hráč ${index + 1}`, `Player ${index + 1}`, `Spieler ${index + 1}`);
  });
  const activeOrder = slots.map((slot,index)=>({faction:slot.faction,roll:rolls[index]??0})).sort((a,b)=>b.roll-a.roll).map(entry=>entry.faction);
  next.turnOrder = [...activeOrder, ...FACTIONS.filter(f => !activeOrder.includes(f))];
  next.currentFaction = activeOrder[0];
  next.roundStarter = activeOrder[0];
  // The D6 roll can select a different side than the scenario authoring default.
  // Start the live turn from a clean activation state for the actual starter.
  next.pieces.forEach(piece => {
    piece.activated = piece.owner !== next.currentFaction;
    if (piece.owner === next.currentFaction) {
      piece.movementUsed = false;
      piece.actionUsed = false;
    }
  });
  next.humanFaction = slots.find(slot=>slot.control==="HUMAN")?.faction??slots[0].faction;
  next.localSpectator=spectator;
  next.event = text(language, `${next.players[next.currentFaction].name} začíná po hodu D6.`, `${next.players[next.currentFaction].name} begins after the D6 roll.`, `${next.players[next.currentFaction].name} beginnt nach dem W6-Wurf.`);
  return next;
}

export function SkirmishScreen({ language, t, start, onBack }: {
  language: Language;
  t: (key: MessageKey) => string;
  start: (state: GameState) => void;
  onBack: () => void;
}) {
  const [selected, setSelected] = useState<string | null>(null);
  const [slots, setSlots] = useState(() => defaults(language));
  const [playerCount,setPlayerCount]=useState(2);
  const [stage, setStage] = useState<"PLAYERS" | "DICE">("PLAYERS");
  const [rolls, setRolls] = useState<number[] | null>(null);
  const [loaded, setLoaded] = useState<GameState | null>(null);
  const [busy, setBusy] = useState(false);
  const [spectator,setSpectator]=useState(false);

  const update = (index: number, patch: Partial<SkirmishSlot>) => {if(patch.control==="SPECTATOR"){setSpectator(true);patch={control:"AI"};}setSlots(current => {
      const next = structuredClone(current);
      const row = next[index];
      if (patch.faction) {
      const other = next.findIndex((slot,slotIndex)=>slotIndex!==index&&slot.faction===patch.faction);
      if (other>=0) next[other].faction = row.faction;
    }
    if (patch.position !== undefined) {
      const other = next.findIndex((slot,slotIndex)=>slotIndex!==index&&slot.position===patch.position);
      if (other>=0) next[other].position = row.position;
    }
    Object.assign(row, patch);
    if (row.control === "AI") row.name = text(language, "Počítač", "Computer", "Computer");
    return next;
  });};

  const openSetup = (id: string) => {
    setSelected(id);
    const count=skirmishPlayerRange[id as PlayableSkirmish][1]===4?4:2;
    setPlayerCount(count);setSlots(defaults(language,count));
    setStage("PLAYERS");
    setSpectator(false);
    setRolls(null);
    setLoaded(null);
  };

  const continueToDice = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      setLoaded(await loadScenario(selected, Date.now()));
      setStage("DICE");
      setRolls(null);
    } finally {
      setBusy(false);
    }
  };

  const roll = () => setRolls(slots.map(()=>Math.floor(Math.random() * 6) + 1));
  const tied = !!rolls && rolls.filter(value=>value===Math.max(...rolls)).length>1;

  return <section className="mode-screen skirmish-screen">
    <div className="mode-heading"><button className="dd-nav-button dd-nav-button--compact" onClick={onBack}>{t("returnMenu")}</button><div className="section-title"><span/><h2>{t("skirmish")}</h2><span/></div><i/></div>
    <div className="card-grid skirmish-grid dd-skirmish-grid">{playableSkirmishes.map(id => <article className="scenario-card dd-skirmish-card" key={id}>
      <div className="dd-skirmish-card__preview"><img src={`${import.meta.env.BASE_URL}assets/${id}.webp`} alt={id.replace("_","-")}/></div>
      <p className="dd-skirmish-card__players">{skirmishPlayerRange[id][0]===skirmishPlayerRange[id][1]?"2":"2–4"} {text(language, "HRÁČI", "PLAYERS", "SPIELER")}</p>
      <h3 className="dd-skirmish-card__title">{id.replace("skirmish_","Skirmish-")}</h3>
      <button className="primary dd-setup-game-button" onClick={() => openSetup(id)}>{text(language, "NASTAVIT HRU", "SET UP GAME", "SPIEL EINRICHTEN")}</button>
    </article>)}</div>
    {selected && <div className="modal-backdrop tactical-dialog-backdrop"><section className="skirmish-setup-dialog">
      <button className="close" aria-label={t("close")} onClick={() => setSelected(null)}>×</button>
      <small>{text(language, "NASTAVENÍ SKIRMISHE", "SKIRMISH SETUP", "GEFECHT EINRICHTEN")}</small><h2>{selected.replace("skirmish_", "Skirmish-")}</h2>
      {stage === "PLAYERS" ? <>
        <p>{text(language, "Mapa a startovní pole jsou pevně dané pravidly. Barvy a pozice se nesmí opakovat; týmy A a B jsou protivníci.", "The map and starting spaces are fixed by the rules. Colours and positions cannot repeat; Teams A and B are opponents.", "Karte und Startfelder sind festgelegt. Farben und Positionen dürfen sich nicht wiederholen; Team A und B sind Gegner.")}</p>
        {selected&&skirmishPlayerRange[selected as PlayableSkirmish][1]===4&&<label className="skirmish-player-count">{text(language,"POČET HRÁČŮ","NUMBER OF PLAYERS","SPIELERZAHL")}<select value={playerCount} onChange={event=>{const count=Number(event.target.value);setPlayerCount(count);setSlots(defaults(language,count));setSpectator(false);}}><option value={2}>2</option><option value={4}>4</option></select></label>}
        <div className="skirmish-player-table"><div className="skirmish-player-head"><b>{text(language, "HRÁČ", "PLAYER", "SPIELER")}</b><b>{text(language, "OVLÁDÁNÍ", "CONTROL", "STEUERUNG")}</b><b>{text(language, "JMÉNO", "NAME", "NAME")}</b><b>{text(language, "BARVA", "COLOUR", "FARBE")}</b><b>{text(language, "POZICE", "POSITION", "POSITION")}</b><b>{text(language, "TÝM", "TEAM", "TEAM")}</b></div>
          {slots.map((slot, index) => <div className="skirmish-player-row" key={index}>
            <strong>{text(language, "HRÁČ", "PLAYER", "SPIELER")} {index + 1}</strong>
            <select value={slot.control} onChange={event => update(index, { control: event.target.value as SkirmishSlot["control"] })}><option value="HUMAN">{index === 0 ? text(language, "TY / ČLOVĚK", "YOU / HUMAN", "DU / MENSCH") : text(language, "ČLOVĚK", "HUMAN", "MENSCH")}</option><option value="AI">{text(language, "AI / POČÍTAČ", "AI / COMPUTER", "KI / COMPUTER")}</option><option value="SPECTATOR">{text(language,"DIVÁK","SPECTATOR","ZUSCHAUER")}</option></select>
            <input disabled={slot.control === "AI"} value={slot.name} onChange={event => update(index, { name: event.target.value })}/>
            <select className="faction-select" style={{ color: FACTION_META[slot.faction].color, borderColor: FACTION_META[slot.faction].color }} value={slot.faction} onChange={event => update(index, { faction: event.target.value as Faction })}>{(["EFA", "REDCAT", "GREEN", "BLUE"] as Faction[]).map(faction => <option style={{ color: FACTION_META[faction].color }} value={faction} key={faction}>{FACTION_META[faction].label}</option>)}</select>
            <select value={slot.position} onChange={event => update(index, { position: Number(event.target.value) })}>{Array.from({length:playerCount},(_,position)=><option value={position} key={position}>{text(language, "POZICE", "POSITION", "POSITION")} {position+1}</option>)}</select>
            <span className="locked-team">{text(language, "TÝM", "TEAM", "TEAM")} {String.fromCharCode(65+index)}{playerCount===2?` · ${text(language, "ZAMČENO", "LOCKED", "GESPERRT")}`:""}</span>
          </div>)}</div>{spectator&&<div className="spectator-row"><strong>PLAYER 3</strong><span>SPECTATOR</span><b>{text(language,"VY · POUZE SLEDOVÁNÍ","YOU · VIEW ONLY","DU · NUR ZUSCHAUEN")}</b><button onClick={()=>{setSpectator(false);update(0,{control:"HUMAN",name:text(language,"Hráč 1","Player 1","Spieler 1")});}}>×</button></div>}
        <div className="dialog-actions"><button onClick={() => setSelected(null)}>{t("cancel")}</button><button className="primary" disabled={busy} onClick={() => void continueToDice()}>{busy ? "…" : text(language, "POKRAČOVAT", "CONTINUE", "WEITER")}</button></div>
      </> : <div className="dice-setup">
        <p>{text(language, "Oba hráči hodí D6; vyšší výsledek začíná.", "Both players roll a D6; the higher result starts.", "Beide Spieler würfeln einen W6; das höhere Ergebnis beginnt.")}</p>
        <div className="dice-results">{slots.map((slot, index) => <article key={index} style={{ borderColor: FACTION_META[slot.faction].color }}><img src={`${import.meta.env.BASE_URL}assets/${FACTION_META[slot.faction].logo}`} alt=""/><strong>{slot.name}</strong><GameDie value={rolls?.[index]}/></article>)}</div>
        {rolls && (tied ? <h3 className="dice-tie">{text(language, "SHODA O PRVNÍ MÍSTO — HOĎTE ZNOVU", "TIE FOR FIRST — ROLL AGAIN", "GLEICHSTAND UM PLATZ EINS — NOCH EINMAL WÜRFELN")}</h3> : <h3>{slots[rolls.indexOf(Math.max(...rolls))].name} {text(language, "ZAČÍNÁ", "STARTS", "BEGINNT")}</h3>)}
        <div className="dialog-actions"><button onClick={() => setStage("PLAYERS")}>‹ {text(language, "HRÁČI", "PLAYERS", "SPIELER")}</button><button disabled={!!rolls && !tied} onClick={roll}><><DiceRollIcon/>{tied ? text(language, "HODIT ZNOVU", "REROLL D6", "NOCH EINMAL WÜRFELN") : text(language, "HODIT D6", "ROLL D6", "W6 WÜRFELN")}</></button><button className="primary" disabled={!rolls || tied || !loaded} onClick={() => loaded && rolls && !tied && start(configureSkirmishState(loaded, slots, rolls, language, spectator))}>{text(language, "SPUSTIT BOJIŠTĚ", "START BATTLEFIELD", "SPIELFELD STARTEN")}</button></div>
      </div>}
    </section></div>}
  </section>;
}
